/**
 * Shared embed URL utilities for VideoEmbed, MapEmbed, and CustomEmbed.
 *
 * Extracted from individual section modules so both the section components
 * (rendering) and the portal inspector (editing) share the same validation
 * and normalization logic.
 */

// ---------------------------------------------------------------------------
// Video embed
// ---------------------------------------------------------------------------

export const VIDEO_EMBED_ALLOWLIST = [
  "www.youtube.com",
  "www.youtube-nocookie.com",
  "player.vimeo.com",
] as const;

/**
 * Converts standard YouTube/Vimeo watch URLs to embed URLs.
 * Validates the result against an allowlist of trusted hosts.
 * Returns "about:blank" for unrecognized or untrusted URLs.
 */
export function toEmbedUrl(url: string): string {
  let embedUrl = url;

  // YouTube: youtube.com/watch?v=ID or youtu.be/ID
  const ytWatch = url.match(
    /(?:youtube\.com\/watch\?.*v=|youtu\.be\/)([\w-]+)/,
  );
  if (ytWatch) {
    embedUrl = `https://www.youtube.com/embed/${ytWatch[1]}`;
  } else {
    // Vimeo: vimeo.com/ID
    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) {
      embedUrl = `https://player.vimeo.com/video/${vimeo[1]}`;
    }
  }

  // Validate against allowlist
  try {
    const parsed = new URL(embedUrl);
    if (
      parsed.protocol !== "https:" ||
      !VIDEO_EMBED_ALLOWLIST.includes(
        parsed.hostname as (typeof VIDEO_EMBED_ALLOWLIST)[number],
      )
    ) {
      return "about:blank";
    }
  } catch {
    return "about:blank";
  }

  return embedUrl;
}

// ---------------------------------------------------------------------------
// Map embed
// ---------------------------------------------------------------------------

export const MAP_EMBED_ALLOWLIST = [
  "www.google.com",
  "maps.google.com",
  "maps.googleapis.com",
] as const;

export function validateMapUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" &&
      MAP_EMBED_ALLOWLIST.includes(
        parsed.hostname as (typeof MAP_EMBED_ALLOWLIST)[number],
      )
    );
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Custom embed (generic HTTPS validation -- no host allowlist)
// ---------------------------------------------------------------------------

export function validateHttpsUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}
